import { safeParseFloat } from "encamp-shared/src/utils/numberParsing/safeParseNumber";
import {
  EpisodicEventManifestsQuery,
  EpisodicManifestCandidatesQuery,
  EpisodicManifestExemptionState,
  EpisodicStreamExemptionInput,
} from "generated-graphql/graphql";

import { formatPounds } from "./episodicDisplay";
import { calculatedOf, declarationOf, EventStreamRow, parseStreamRow } from "./eventManifestRows";

export type EventManifestExemptions = NonNullable<
  EpisodicEventManifestsQuery["episodicEvent"]
>["manifestExemptions"];
export type ExemptManifest = EventManifestExemptions["manifests"][number];
export type ExemptStream = ExemptManifest["streams"][number];
export type ManifestCandidate = NonNullable<
  EpisodicManifestCandidatesQuery["episodicEvent"]
>["manifestCandidates"][number];

export function streamCapPounds(stream: {
  groupPounds: number;
  exemptPoundsOnOtherEvents: number;
}): number {
  return Math.max(0, stream.groupPounds - stream.exemptPoundsOnOtherEvents);
}

export const MANIFEST_REFETCH_QUERIES = ["EpisodicEventManifests", "FacilityEpisodicEvents"];

export type StreamAmountDraft = {
  wasteStreamId: string;
  name: string;
  groupPounds: number;
  capPounds: number;
  /** Pounds other events' applied claims take from the stream before the shared cap prorates them. */
  otherEventsPounds: number;
  matchingWasteCodes: string[];
  /** The stream's first shipment on this manifest, which the server judges the window on. */
  firstShipped: string;
  declarable: boolean;
  /** Pounds this event already holds on the stream, or null; a locked stream's are removed on save. */
  declaredPounds: number | null;
  containerPounds: number | null;
  unweighedContainerCount: number;
  overriding: boolean;
  pounds: string;
};

/**
 * A stream the user cannot declare on: shipped outside the window, or held entirely by other
 * events. It declares nothing, so saving the manifest drops any pounds this event had on it.
 */
export function isLockedDraft(draft: Pick<StreamAmountDraft, "declarable" | "capPounds">): boolean {
  return !draft.declarable || draft.capPounds <= 0;
}

function existingRows(existing: ExemptManifest | null): EventStreamRow[] {
  return existing?.streams.flatMap((stream) => parseStreamRow(existing, stream) ?? []) ?? [];
}

const toHundredths = (pounds: number) => Math.round(pounds * 100) / 100;

function initialPounds(
  declaredPounds: number | null,
  containerPounds: number | null,
  capPounds: number,
  locked: boolean
): string {
  if (locked) {
    return "0";
  }
  if (declaredPounds !== null) {
    return String(declaredPounds);
  }
  return containerPounds === null
    ? "0"
    : String(toHundredths(Math.min(containerPounds, capPounds)));
}

export function draftsForManifest(
  candidate: ManifestCandidate,
  existing: ExemptManifest | null,
  overrideStreamId: string | null = null
): StreamAmountDraft[] {
  const rows = existingRows(existing);
  return candidate.streams.map((stream) => {
    const row = rows.find((r) => r.wasteStream.id === stream.wasteStream.id);
    const declared = row ? declarationOf(row.basis) : null;
    const calculated = row ? calculatedOf(row.basis) : null;
    const capPounds = streamCapPounds(stream);
    const declaredPounds = declared?.declaredPounds ?? null;
    const containerPounds = stream.calculatedPoundsForEvent ?? null;
    const locked = isLockedDraft({ declarable: stream.declarable, capPounds });
    return {
      wasteStreamId: stream.wasteStream.id,
      name: stream.wasteStream.name,
      groupPounds: stream.groupPounds,
      capPounds,
      otherEventsPounds: stream.exemptPoundsOnOtherEvents,
      matchingWasteCodes: stream.matchingWasteCodes,
      firstShipped: stream.firstShipped,
      declarable: stream.declarable,
      declaredPounds,
      containerPounds,
      unweighedContainerCount: calculated?.unweighedContainerCount ?? 0,
      overriding:
        containerPounds === null ||
        declaredPounds !== null ||
        stream.wasteStream.id === overrideStreamId,
      pounds: initialPounds(declaredPounds, containerPounds, capPounds, locked),
    };
  });
}

/** Over the cap as displayed: the row shows the cap to the hundredth, so that hundredth is allowed. */
function overCap(pounds: number, draft: StreamAmountDraft): boolean {
  return toHundredths(pounds) > toHundredths(draft.capPounds);
}

/**
 * The prefilled container total, still as prefilled. Saving it as a declaration would freeze the
 * figure, so later corrections to the containers' weights would never reach status.
 */
function isUnchangedContainerTotal(pounds: number, draft: StreamAmountDraft): boolean {
  return (
    draft.declaredPounds === null &&
    draft.containerPounds !== null &&
    toHundredths(pounds) === toHundredths(Math.min(draft.containerPounds, draft.capPounds))
  );
}

/**
 * The typed amount when it is a usable number within the cap; 0, blank, a locked stream and an
 * unchanged container total are "nothing declared".
 */
function parsedPounds(draft: StreamAmountDraft): number | null {
  if (isLockedDraft(draft) || !draft.overriding) {
    return 0;
  }
  const pounds = draft.pounds.trim() === "" ? 0 : safeParseFloat(draft.pounds);
  if (pounds == null || pounds < 0 || overCap(pounds, draft)) {
    return null;
  }
  return isUnchangedContainerTotal(pounds, draft) ? 0 : pounds;
}

/** The pounds this draft declares, or null when it declares nothing or is invalid. */
export function draftPounds(draft: StreamAmountDraft): number | null {
  const pounds = parsedPounds(draft);
  return pounds === null || pounds === 0 ? null : pounds;
}

export function draftError(draft: StreamAmountDraft): string | null {
  if (isLockedDraft(draft) || !draft.overriding) {
    return null;
  }
  const pounds = draft.pounds.trim() === "" ? 0 : safeParseFloat(draft.pounds);
  if (pounds == null || pounds < 0) {
    return "Enter 0 lb or more";
  }
  if (overCap(pounds, draft)) {
    if (draft.capPounds <= 0) {
      return `Other events already hold all of ${draft.name} on this manifest`;
    }
    return draft.capPounds < draft.groupPounds
      ? `${formatPounds(draft.capPounds)} of ${draft.name} left after other events`
      : `Only ${formatPounds(draft.capPounds)} of ${draft.name} is on this manifest`;
  }
  return null;
}

/** Every row is a valid amount and at least one is above zero. */
export function canSaveDrafts(drafts: StreamAmountDraft[]): boolean {
  return (
    drafts.every((draft) => parsedPounds(draft) !== null) &&
    drafts.some((draft) => draftPounds(draft) !== null)
  );
}

export function draftsToInputs(drafts: StreamAmountDraft[]): EpisodicStreamExemptionInput[] {
  return drafts.flatMap((draft) => {
    const pounds = draftPounds(draft);
    return pounds == null ? [] : [{ wasteStreamId: draft.wasteStreamId, pounds }];
  });
}

export function runningTotal(
  currentTotal: number,
  existing: ExemptManifest | null,
  drafts: StreamAmountDraft[],
  reliefApplies = true
): { before: number; after: number } {
  // Where relief does not reach the facility the server stores the rows but
  // exempts nothing, so the projection must not promise an increase.
  if (!reliefApplies || !canSaveDrafts(drafts)) {
    return { before: currentTotal, after: currentTotal };
  }
  const draftSum = drafts.reduce((sum, draft) => sum + (draftPounds(draft) ?? 0), 0);
  const declaredOn = new Set(
    drafts.flatMap((draft) => (draftPounds(draft) === null ? [] : [draft.wasteStreamId]))
  );
  const otherEventsPounds = new Map(
    drafts.map((draft) => [draft.wasteStreamId, draft.otherEventsPounds])
  );
  const containersKeep = existingRows(existing)
    .filter((row) => !declaredOn.has(row.wasteStream.id))
    .reduce(
      (sum, row) =>
        sum + containerFallbackPounds(row, otherEventsPounds.get(row.wasteStream.id) ?? 0),
      0
    );
  return {
    before: currentTotal,
    after: currentTotal - (existing?.exemptPounds ?? 0) + draftSum + containersKeep,
  };
}

/**
 * What the event's containers exclude once its declaration on the stream is dropped. Mirrors the
 * server's shared cap: every applied claim on the stream is scaled down together when they exceed
 * what the manifest carries.
 */
function containerFallbackPounds(row: EventStreamRow, otherEventsPounds: number): number {
  switch (row.basis.kind) {
    case "calculated":
      return row.exemptPounds;
    case "declared":
      return 0;
    case "declaredOverCalculated":
      return row.state === EpisodicManifestExemptionState.Applied
        ? cappedShare(row.basis.calculated.pounds, otherEventsPounds, row.manifestPounds)
        : 0;
  }
}

function cappedShare(
  claimPounds: number,
  otherEventsPounds: number,
  manifestPounds: number
): number {
  const total = claimPounds + otherEventsPounds;
  return claimPounds > 0 ? (Math.min(total, manifestPounds) * claimPounds) / total : 0;
}

const REPLACES_CONTAINER_TOTAL =
  "Your entry replaces the container total for this stream. You can switch back later.";

export function containerDraftNote(draft: StreamAmountDraft): string | null {
  if (draft.containerPounds === null || isLockedDraft(draft)) {
    return null;
  }
  const unweighed = draft.unweighedContainerCount;
  if (unweighed > 0 && draft.declaredPounds === null) {
    const containers = unweighed === 1 ? "1 container" : `${unweighed} containers`;
    return `${formatPounds(draft.containerPounds)} is what the weighed containers shipped. Include the pounds of the ${containers} with no weight. ${REPLACES_CONTAINER_TOTAL}`;
  }
  return `Containers total ${formatPounds(draft.containerPounds)}. ${REPLACES_CONTAINER_TOTAL}`;
}
